import { DateTimePicker12 } from './TimePicker12';
import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { assignDelivery, getDeliveryStaff } from '../api';
import DeliveryTicketPanel from './DeliveryTicketPanel';

// Owner Bookings & Cancellation v2 doc, Story 14 - the owner (or admin) assigns a delivery-role
// account and, optionally, a scheduled date to a confirmed booking. Popup, not a bare prompt -
// same pattern the rest of this doc's flows use.
//
// Story 16 - the booking's own deliveryAddress is shown so the owner knows where this is actually
// going, and its area/city is passed to getDeliveryStaff so whoever covers that area is sorted to
// the top of the list and badged, rather than picking blind.
//
// Story 17 - the booking's scheduled/start date is also passed, so a candidate with an
// overlapping unavailability window shows `available: false` instead of looking like anyone else.
// `suggestedDeliveryPersonId` (optional) pre-selects a candidate - used when this modal is opened
// via a "Reassign" action after a decline (Story 17's "without hassle" ask), so the owner/admin
// doesn't have to re-pick from scratch.
export default function AssignDeliveryModal({ booking, show, onHide, onAssigned, suggestedDeliveryPersonId }) {
  const [staff, setStaff] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [deliveryPersonId, setDeliveryPersonId] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show) return;
    setError('');
    setDeliveryPersonId(booking?.deliveryAssignment?.assignedTo?._id || suggestedDeliveryPersonId || '');
    setScheduledAt(booking?.deliveryAssignment?.scheduledAt ? booking.deliveryAssignment.scheduledAt.slice(0, 16) : '');
    setNotes(booking?.deliveryAssignment?.notes || '');
    setLoadingStaff(true);
    const area = booking?.deliveryAddress?.area || booking?.deliveryAddress?.city || '';
    const date = booking?.deliveryAssignment?.scheduledAt || booking?.startDate || '';
    getDeliveryStaff(area, date)
      .then(setStaff)
      .catch(() => setError('Could not load delivery staff.'))
      .finally(() => setLoadingStaff(false));
  }, [show, booking, suggestedDeliveryPersonId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!deliveryPersonId) {
      setError('Please choose a delivery person.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const result = await assignDelivery(booking._id, {
        deliveryPersonId,
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        notes: notes.trim()
      });
      onAssigned(result);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to assign delivery.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!booking) return null;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Assign delivery</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {booking?.deliveryTicket ? (
            <DeliveryTicketPanel ticket={booking.deliveryTicket} className="mb-2" />
          ) : booking?.deliveryAddress?.line1 && (
            <div className="small text-muted mb-2">
              Delivering to: {[booking.deliveryAddress.line1, booking.deliveryAddress.line2, booking.deliveryAddress.area, booking.deliveryAddress.city, booking.deliveryAddress.pincode].filter(Boolean).join(', ')}
            </div>
          )}
          <div className="small text-muted mb-2">
            Pickup uses your saved pickup address (set it on your Owner dashboard). The delivery
            distance is refreshed when you assign.
          </div>
          {loadingStaff && <Spinner animation="border" size="sm" role="status" />}
          {error && <Alert variant="danger" className="py-2">{error}</Alert>}
          {!loadingStaff && staff.length === 0 && (
            <Alert variant="light" className="py-2">
              No delivery staff accounts exist yet - ask an admin to add one from Admin → Users.
            </Alert>
          )}
          <Form.Group className="mb-2" controlId="assign-delivery-person">
            <Form.Label>Delivery person</Form.Label>
            <Form.Select value={deliveryPersonId} onChange={(e) => setDeliveryPersonId(e.target.value)} required>
              <option value="">Choose...</option>
              {staff.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name} {s.phone ? `(${s.phone})` : ''}
                  {s.matchesArea ? ' - serves this area' : ''}
                  {s.available === false ? ' - may be unavailable' : ''}
                </option>
              ))}
            </Form.Select>
            {(() => {
              const selected = staff.find((s) => s._id === deliveryPersonId);
              if (!selected) return null;
              return (
                <div className="mt-1">
                  {selected.matchesArea && <Badge bg="success" className="me-1">Serves this area</Badge>}
                  {selected.available === false && <Badge bg="warning" text="dark">May be unavailable on this date</Badge>}
                </div>
              );
            })()}
          </Form.Group>
          <Form.Group className="mb-2" controlId="assign-delivery-schedule">
            <Form.Label>Scheduled date/time (optional)</Form.Label>
            <DateTimePicker12  value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-2" controlId="assign-delivery-notes">
            <Form.Label className="small">Notes (optional)</Form.Label>
            <Form.Control as="textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Assigning…' : 'Assign'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
